import { useQueryClient } from "@tanstack/react-query";
import { type RefObject, useRef } from "react";
import { toast } from "sonner";
import { removeOptimisticChatMessage } from "web_myplann/Chat/Helpers/chatMessagesCache";
import { createChatClientId } from "web_myplann/Chat/Helpers/createChatClientId";
import { restoreChatAttachmentFile } from "web_myplann/Chat/Helpers/restoreChatAttachmentFile";
import type { ChatMessageView } from "web_myplann/Chat/Types/chatMessage";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { base64ToFile } from "web_myplann/Infrastructure/Utils/FileUtils";
import { device } from "web_myplann/Utils/device";

type AttachmentPicker = () => Promise<{
	payload: { file: { base64: string; filename: string } };
}>;

type Args = {
	conversationIdRef: RefObject<number | null>;
	takeCaption: () => string;
	sendText: (content: string, clientId: string) => void;
	sendAttachment: (
		file: File,
		caption: string,
		clientId: string,
		callbacks: { onSettled: () => void },
	) => void;
	onMessageSent: () => void;
};

export const useChatAttachments = ({
	conversationIdRef,
	takeCaption,
	sendText,
	sendAttachment,
	onMessageSent,
}: Args) => {
	const queryClient = useQueryClient();
	const { t } = useResource();
	const pendingFilesRef = useRef(new Map<string, File>());

	const sendTrackedAttachment = (
		file: File,
		caption: string,
		clientId: string,
	) => {
		pendingFilesRef.current.set(clientId, file);

		sendAttachment(file, caption, clientId, {
			onSettled: () => pendingFilesRef.current.delete(clientId),
		});
		onMessageSent();
	};

	const sendPickedAttachment = async (picker: AttachmentPicker) => {
		if (conversationIdRef.current === null) return;

		const response = await picker();
		const base64 = response.payload.file.base64;
		const filename = response.payload.file.filename;
		if (!base64 || !filename) return;

		sendTrackedAttachment(
			base64ToFile(base64, filename),
			takeCaption(),
			createChatClientId(),
		);
	};

	const handleRetry = async (message: ChatMessageView) => {
		const clientId = message.clientId;
		if (clientId === undefined) return;

		const attachment = message.attachments.at(0);
		const cachedFile = pendingFilesRef.current.get(clientId);
		const file = attachment
			? await restoreChatAttachmentFile(attachment, cachedFile)
			: undefined;

		if (attachment && !file) {
			toast.error(t(Resources.Chat.sendError));
			return;
		}

		removeOptimisticChatMessage(
			queryClient,
			conversationIdRef.current,
			clientId,
		);
		if (attachment?.localUrl) URL.revokeObjectURL(attachment.localUrl);

		if (file) {
			sendTrackedAttachment(file, message.content, clientId);
			return;
		}

		sendText(message.content, clientId);
		onMessageSent();
	};

	return {
		onRetry: handleRetry,
		onPickPhoto: () => sendPickedAttachment(device.pickAttachmentPhoto),
		onPickGallery: () => sendPickedAttachment(device.pickAttachmentGallery),
		onPickFile: () => sendPickedAttachment(device.pickAttachmentFile),
	};
};
